import { Button } from '@dynamika-shared/ui/Button/Button';
import { Attention, RefreshIcon } from '@dynamika-shared/ui/icons';
import { CircleDivider } from '@dynamika-shared/ui/CircleDivider/CircleDivider';
import {
  parseDateOnly,
  shortDate,
  shortDateTime,
} from '@dynamika-shared/utils/date';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CollapseHeader } from '../components/CollapseHeader';
import { Collapse } from '@dynamika-shared/ui/Collapse/Collapse';
import { Table } from 'antd';
import { makeColumns } from './Table.columns';
import {
  DXAReading,
  DXAWorkstationDataDTO,
} from '@dynamika-shared/types/DXAReading';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import _ from 'lodash';
import { SkeletonLine } from '@dynamika-shared/ui/Skeleton/Skeleton';
import { twJoin } from '@dynamika-shared/utils/twMerge';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { SearchSuffix } from '@dynamika-shared/ui/Input/SearchSuffix';
import { highlightFoundText } from 'src/utils/highlightFoundText';
import {
  assignPrefill,
  pickPrefilledGroupIndex,
} from 'src/utils/dxaLabelPrefill';
import { canPrefill, countActiveSelections } from 'src/utils/dxaSelection';
import './Table.scss';
import { useFilters } from './Filters';
import { BottomConfirmation } from 'src/components/BottomConfirmation/BottomConfirmation';
import { NoContent } from '@dynamika-shared/ui/NoContent/NoContent';
import { ExecutionStatus } from '@dynamika-shared/types/executions';
import { ContextData } from '@dynamika-shared/types/clinicalTrialContext';

type Lab = ContextData['configuration']['labelGroups'][0]['labels'][0];

export type Label = {
  id: string;
  name: string;
  dataType: Lab['dataType'];
  modalityName: string;
};

const VIRTUAL_ROWS_QTY = 10;
const ROW_HEIGHT = 77;

type WorkstationResultsTableProps = {
  onSync: () => void;
  sendToReadings: (val: DXAReading[]) => void;
  lastSync: string;
  syncStatus: ExecutionStatus;
  labels: Label[];
  workstationData: DXAWorkstationDataDTO[];
  loading: boolean;
  readOnly: boolean;
};

export const WorkstationResultsTable: FC<WorkstationResultsTableProps> = ({
  workstationData,
  lastSync,
  syncStatus,
  sendToReadings,
  onSync,
  labels,
  loading,
  readOnly = false,
}) => {
  const [collapsedIndex, setCollapsedIndex] = useState(0);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [searchValue, setSearchValue, debouncedValue] = useDebouncedValue('');
  const touchedRowsRef = useRef<Set<string>>(new Set());

  const filteredLabels = useMemo(() => {
    const selectedLabs = Object.values(selected);
    return labels.filter(l => !selectedLabs.includes(l.id));
  }, [labels, selected]);

  const onSelect = (readingId: string, labelId: string) => {
    touchedRowsRef.current.add(readingId);
    const s = { ...selected };
    setSelected(prev => {
      if (prev[readingId] === labelId) {
        delete s[readingId];
        return s;
      }
      return { ...prev, [readingId]: labelId };
    });
  };

  const workstationReadings = useMemo(() => {
    return loading
      ? []
      : workstationData
          .flatMap(data => data?.dxaScores || [])
          .map((r, i) => ({
            ...r,
            recordId:
              r.recordId ??
              `${r.acquisitionDate}_${r.bodyProperty}_${r.bodyRegion}_${i}`,
          }));
  }, [workstationData, loading]);

  const syncCompleted = canPrefill(syncStatus, lastSync);

  useEffect(() => {
    if (!syncCompleted) return;
    if (!workstationReadings.length || !labels.length) return;
    const eligibleRows = workstationReadings.filter(
      r =>
        r.recordId &&
        !touchedRowsRef.current.has(r.recordId) &&
        !selected[r.recordId]
    );
    if (!eligibleRows.length) return;
    const usedLabelIds = new Set(Object.values(selected));
    const availableLabels = labels.filter(l => !usedLabelIds.has(l.id));
    if (!availableLabels.length) return;
    const assignments = assignPrefill(eligibleRows, availableLabels);
    if (!Object.keys(assignments).length) return;
    setSelected(prev => ({ ...prev, ...assignments }));

    // Expand the acquisition-date table that received the pre-fill instead of
    // the default first table. NOTE: `workstationReadingEntries` is read here
    // (in the post-render effect) and is consistent because `filteredReadings`
    // is computed synchronously via useMemo in the same render — keep it that
    // way.
    const prefilledIndex = pickPrefilledGroupIndex(
      workstationReadingEntries,
      assignments
    );
    if (prefilledIndex !== -1) {
      setCollapsedIndex(prefilledIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstationReadings, labels, syncCompleted]);

  const { filterSection, filteredReadings } = useFilters(
    workstationReadings,
    'mr-2'
  );

  const foundReadings = useMemo(() => {
    if (debouncedValue.length < 3) {
      return filteredReadings;
    }

    const hasValue = <T,>(val: T) =>
      String(val).toLowerCase().includes(searchValue.toLowerCase());

    return (filteredReadings ?? [])
      .filter(reading =>
        Object.values(reading).some(value => {
          if (value instanceof Object) {
            return Object.values(value).some(hasValue);
          }
          return hasValue(value);
        })
      )
      .map(reading => highlightFoundText(reading, debouncedValue));
  }, [filteredReadings, debouncedValue]);

  const workstationReadingsGrouped = useMemo(() => {
    if (loading) {
      return {
        a: [],
        b: [],
        c: [],
      };
    }
    return _.groupBy(foundReadings, reading => {
      // Use the calendar date as-is for date-only values so readings are not
      // grouped under the previous day in negative-offset timezones (D7S-528).
      const date =
        parseDateOnly(reading.acquisitionDate) ??
        new Date(reading.acquisitionDate);
      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      ).toISOString();
    });
  }, [foundReadings, loading]);

  // ISO date keys sort chronologically. The first group is open by default, so
  // descending order also opens the most recent acquisition automatically.
  const workstationReadingEntries = Object.entries(
    workstationReadingsGrouped
  ).sort(([dateA], [dateB]) => dateB.localeCompare(dateA));

  const presentRecordIds = useMemo(
    () => new Set(workstationReadings.map(r => r.recordId)),
    [workstationReadings]
  );

  const selectedLength = useMemo(
    () => countActiveSelections(selected, presentRecordIds),
    [selected, presentRecordIds]
  );

  const onSend = () => {
    if (Object.keys(selected).length) {
      const dataToSend = workstationReadings
        .filter(r => !!selected[r.recordId])
        .map(r => ({ ...r, labelId: selected[r.recordId] }));
      sendToReadings(dataToSend);
      setSelected({});
    }
  };

  let syncDateTime = 'No Data';

  if (syncStatus === 'IN_PROGRESS') {
    syncDateTime = 'In Progress';
  }

  if (lastSync) {
    syncDateTime = shortDateTime(lastSync);
  }

  return (
    <div>
      <div className='flex justify-between items-center  w-full'>
        <div className='flex items-center w-full'>
          <h3 className='text-[2.4rem] leading-[3.2rem] font-semibold my-4'>
            Workstation Results
          </h3>
          {readOnly && (
            <span className='mx-[0.6rem]'>
              <Attention className='text-vividTangelo' />
            </span>
          )}
          <CircleDivider />
          <div className='flex text-1624 leading-6 font-medium mr-4'>
            <span>Last Sync:</span>

            <span className='text-dimGray ml-1'>{syncDateTime}</span>
          </div>
          {syncStatus === 'IN_PROGRESS' && (
            <div className='flex items-center text-1420 mr-4'>
              <RefreshIcon
                className={twJoin(
                  'h-[14px] w-[14px] mr-[.8rem] text-lg text-dodgerBlue animate-spin'
                )}
              />
              <div className='text-dodgerBlue'>Synchronizing</div>
            </div>
          )}

          {syncStatus !== 'IN_PROGRESS' && !readOnly && (
            <Button
              type='primary'
              onClick={onSync}
              className='!bg-blackOlive rounded-md items-center hover:border-waterLight border-black !px-1.5 !py-1 hover:!bg-waterLight hover:!text-black'
            >
              <RefreshIcon
                className={twJoin('h-[14px] w-[14px] mr-[.8rem] text-lg')}
              />
              <span className='font-normal text-1420'>Sync</span>
            </Button>
          )}
        </div>
        <div className='flex justify-between'>
          {filterSection}
          <Input
            value={searchValue}
            onChange={e => {
              setSearchValue(e.target.value);
            }}
            className='!mt-0 h-[4.8rem] min-w-[32rem] !rounded-[3.2rem] px-2'
            placeholder='Search'
            suffix={
              <SearchSuffix
                disabled={!searchValue}
                action={() => {
                  setSearchValue('');
                }}
              />
            }
          />
        </div>
      </div>

      <div>
        {!workstationReadingEntries.length && (
          <NoContent
            iconName={'noContent'}
            title={
              <span className='text-spanishGray'>
                No <b className='text-black'>Workstation Results</b>{' '}
                synchronized yet!
              </span>
            }
          >
            {' '}
          </NoContent>
        )}
        {workstationReadingEntries.map(([date, readings], i) => {
          const columns = makeColumns({
            allLabels: labels,
            availableLabels: filteredLabels,
            onSelect,
            isWorkstation: true,
            selected,
            readOnly,
          });

          const header = (
            <div className='flex items-center text-1624 font-semibold'>
              <span>Acquisition Date:</span>{' '}
              <span className='text-dimGray ml-1'>{shortDate(date)}</span>
            </div>
          );

          const headerSkeleton = (
            <div className='flex items-center text-1624 font-semibold'>
              <SkeletonLine length={140} />
              <SkeletonLine length={170} className='ml-1' />
            </div>
          );

          const isCollapsed = `collapse_${collapsedIndex}` !== `collapse_${i}`;
          return (
            <div key={date} className='mb-1'>
              <Collapse
                open={!isCollapsed}
                hideChevron
                onClick={() => setCollapsedIndex(!isCollapsed ? -1 : i)}
                resize={readings.length}
                label={
                  <CollapseHeader
                    withBorder={false}
                    collapseValue={isCollapsed ? 'closed' : 'open'}
                    title={loading ? headerSkeleton : header}
                  />
                }
              >
                <div
                  id='DXA_Workstation_Readings'
                  className='rounded-b-sm border-t-0 '
                >
                  <Table
                    pagination={false}
                    rowKey='recordId'
                    loading={loading}
                    virtual={readings.length > VIRTUAL_ROWS_QTY}
                    columns={columns}
                    scroll={
                      readings.length > VIRTUAL_ROWS_QTY
                        ? { y: ROW_HEIGHT * VIRTUAL_ROWS_QTY }
                        : undefined
                    }
                    dataSource={readings as DXAReading[]}
                  />
                </div>
              </Collapse>
            </div>
          );
        })}
      </div>
      {!!selectedLength && (
        <BottomConfirmation
          onCancel={() => setSelected({})}
          cancelText='Clear Selection'
          cancelProps={{
            type: 'link',
          }}
          onOk={onSend}
          className='bottom-1 mb-1'
        >
          <span className='text-dodgerBlue'>{selectedLength}</span>{' '}
          {selectedLength > 1 ? 'values' : 'value'} selected for reading
        </BottomConfirmation>
      )}
    </div>
  );
};
